import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { updatePageSEO, getSiteUrl } from '../../utils/seo';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = getSiteUrl();
    updatePageSEO({
      title: 'ご予約・お問い合わせ | おかやま美容室 - 岡山市北区の美容室',
      description: '岡山市北区の美容室おかやま美容室へのご予約・お問い合わせ。ご予約、ご相談はお電話またはお問い合わせフォームから。営業時間9:00-18:00、定休日は日曜日・祝日。岡山市北区○○町1-2-3。',
      keywords: '岡山市 美容室, おかやま美容室, お問い合わせ, 予約, 電話, アクセス, 岡山市北区',
      canonical: `${siteUrl}/contact`,
      ogTitle: 'ご予約・お問い合わせ | おかやま美容室 - 岡山市北区の美容室',
      ogDescription: '岡山市北区の美容室おかやま美容室へのご予約・お問い合わせ。ご予約、ご相談はお気軽にどうぞ。',
      ogType: 'website',
      ogUrl: `${siteUrl}/contact`,
      ogImage: 'https://public.readdy.ai/ai/img_res/c702c077156e6e38c968fd861e85e54e.jpg',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'ご予約・お問い合わせ',
        description: '岡山市北区の美容室おかやま美容室へのご予約・お問い合わせページ',
        url: `${siteUrl}/contact`,
        mainEntity: {
          '@type': 'HairSalon',
          name: 'おかやま美容室株式会社',
          telephone: '+81-86-012-3456',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '○○町1-2-3',
            addressLocality: '岡山市北区',
            addressRegion: '岡山県',
            postalCode: '700-0000',
            addressCountry: 'JP',
          },
        },
      },
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      alert('お名前、メールアドレス、お問い合わせ内容は必須項目です。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5kajhideasqilubforg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-96 sm:h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/6d554a1fcc0aa72dd8d5ae7deb51101f.jpeg"
              alt="お問い合わせ - 岡山市の美容室おかやま美容室"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              CONTACT
            </h1>
            <p className="text-base sm:text-lg text-white tracking-widest" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              ご予約・お問い合わせ
            </p>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed max-w-3xl mx-auto">
                岡山市の美容室おかやま美容室へのご予約・お問い合わせは、<br className="hidden sm:block" />
                お電話またはお問い合わせフォームよりお気軽にご連絡ください。
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              {/* Contact Form */}
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-8 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  ご予約・お問い合わせフォーム
                </h2>
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      placeholder="山田 太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                      お問い合わせ種別
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="予約">予約について</option>
                      <option value="サービス">サービス内容について</option>
                      <option value="料金">料金について</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm resize-none"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {formData.message.length} / 500文字
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800">
                        お問い合わせありがとうございます。内容を確認次第、ご連絡させていただきます。
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800">
                        送信に失敗しました。お手数ですが、お電話にてお問い合わせください。
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gray-900 text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
